import React, { use } from 'react';
import { ThemeContext } from '../hooks/themeContextTypes';
import type { NewsCategory } from '../hooks/useCategory';

interface AppHeaderProps {
  selectedCategory: NewsCategory;
  setSelectedCategory: (category: NewsCategory) => void;
}

const CATEGORIES: { key: NewsCategory; label: string }[] = [
  { key: 'general', label: 'General' },
  { key: 'world', label: 'World' },
  { key: 'technology', label: 'Technology' },
];

export const AppHeader: React.FC<AppHeaderProps> = ({
  selectedCategory,
  setSelectedCategory,
}) => {
  const { theme, setTheme } = use(ThemeContext)!;

  return (
    <header className="sticky top-0 z-50 shadow-sm">
      {/* Branding row */}
      <div className="bg-white border-b-4 border-red-700">
        <div className="max-w-7xl mx-auto px-3 sm:px-8 lg:px-10">
          <div className="flex items-center justify-between h-11 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <div className="w-1 h-7 sm:h-10 bg-red-700 rounded-sm flex-shrink-0"></div>
              <div className="min-w-0">
                <h1 className="text-base sm:text-xl font-bold text-gray-900 leading-none tracking-tight truncate">
                  AI News Hub
                </h1>
                <p className="text-xs text-gray-500 mt-0.5 hidden sm:block">
                  Your trusted source for AI &amp; technology news
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs text-gray-400 uppercase tracking-wider hidden sm:block">Theme</span>
              <div className="theme-switcher flex items-center gap-1">
                {(['classic', 'dark', 'coder'] as const).map(t => (
                  <button
                    key={t}
                    onClick={() => setTheme(t)}
                    className={`px-2 sm:px-3 py-1 text-xs font-medium rounded capitalize ${theme === t ? 'active' : ''}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Category nav row */}
      <div className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-3 sm:px-8 lg:px-10">
          <nav className="flex items-center gap-5 sm:gap-8 h-9 sm:h-11 overflow-x-auto scrollbar-none">
            {CATEGORIES.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setSelectedCategory(key)}
                className={`text-xs sm:text-sm font-medium h-full border-b-2 transition-colors duration-200 whitespace-nowrap ${
                  selectedCategory === key
                    ? 'text-gray-900 font-bold border-red-700'
                    : 'text-gray-500 border-transparent hover:text-gray-900 hover:border-gray-300 cursor-pointer'
                }`}
              >
                {label}
              </button>
            ))}
          </nav>
        </div>
      </div>
    </header>
  );
};
